<template>
  <oxd-table-filter :filter-title="$t('payroll.deduction_rules')">
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.deductionName"
              :label="$t('payroll.deduction_name')"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.deductionType"
              type="select"
              :options="typeOptions"
              :label="$t('payroll.deduction_type')"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          type="reset"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <div>
        <oxd-button
          :label="$t('general.add')"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
    </div>
    <table-header
      :total="total"
      :loading="isLoading"
      :selected="checkedItems.length"
      @delete="onClickDeleteSelected"
    />
    <div class="orangehrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        :headers="headers"
        :items="items?.data"
        :selectable="true"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
    <delete-confirmation ref="deleteDialog" />
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {translate as translatorFactory} from '@/core/plugins/i18n/translate';
import {payrollT} from '@/orangehrmPayrollPlugin/util/payrollI18n';

const translate = translatorFactory();

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const filters = ref({
      deductionName: '',
      deductionType: null,
    });

    const serializedFilters = computed(() => {
      return {
        deductionName: filters.value.deductionName,
        deductionType: filters.value?.deductionType?.param,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/global-deduction-rules',
    );

    const normalizer = (data) => {
      return data.map((item) => {
        const isTaxTer =
          item.deductionType === 'tax' && item.calculationMethod === 'ter';
        const isLateTieredPct =
          item.deductionType === 'late' &&
          item.calculationMethod === 'tiered' &&
          item.isPercentage === true;
        const showAsPercentage =
          item.isPercentage === true &&
          item.deductionType !== 'tax' &&
          !isLateTieredPct;
        const formattedAmount = showAsPercentage
          ? `${item.amount}%`
          : item.currency?.id
          ? `${item.amount} (${item.currency.id})`
          : `${item.amount}`;
        return {
          id: item.id,
          deductionName: item.deductionName,
          deductionType: item.deductionType,
          amount: isTaxTer || isLateTieredPct ? '-' : formattedAmount,
          status: item.isActive
            ? translate('general.yes')
            : translate('general.no'),
        };
      });
    };

    const {
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer,
    });

    return {
      http,
      total,
      pages,
      filters,
      pageSize,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
      items: response,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'deductionName',
          slot: 'title',
          title: this.$t('payroll.deduction_name'),
          style: {flex: 1},
        },
        {
          name: 'deductionType',
          slot: 'title',
          title: this.$t('payroll.deduction_type'),
          style: {flex: 1},
        },
        {
          name: 'amount',
          slot: 'title',
          title: this.$t('payroll.deduction_amount'),
          style: {flex: 1},
        },
        {
          name: 'status',
          slot: 'title',
          title: this.$t('payroll.active'),
          style: {flex: 1},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionRenderer,
        },
      ],
      checkedItems: [],
      typeOptions: [
        {id: 1, param: 'tax', label: this.$t('payroll.tax')},
        {id: 2, param: 'insurance', label: this.$t('payroll.insurance')},
        {id: 3, param: 'pension', label: this.$t('payroll.pension')},
        {id: 4, param: 'other', label: this.$t('payroll.other')},
        {
          id: 5,
          param: 'late',
          label: payrollT(this.$t, 'payroll.deduction_type_late'),
        },
      ],
    };
  },

  methods: {
    onClickAdd() {
      navigate('/payroll/addPayrollGlobalDeductionRule');
    },
    actionRenderer(...[, , , row]) {
      const cellConfig = {};
      cellConfig.edit = {
        onClick: () => this.onClickEdit(row),
        props: {name: 'pencil-fill'},
      };
      cellConfig.delete = {
        onClick: () => this.onClickDelete(row),
        component: 'oxd-icon-button',
        props: {name: 'trash'},
      };
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onClickEdit(item) {
      navigate('/payroll/editPayrollGlobalDeductionRule/{id}', {id: item.id});
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => this.items?.data[index].id);
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({ids: items})
          .then(() => this.$toast.deleteSuccess())
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>
